/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { clientUtil, Logger } from "../../core/libs";
import { HorizonIndexedDBService } from "../../core/services/storage/horizon-indexed-db.service";
import { LocalStorageService } from "@html-core";

@Injectable({
   providedIn: "root"
})
export class LogFileService {
   private static readonly dateBaseName = "HorizonLogFile";
   private static readonly objectStoreName = "__HORIZON_LOG_FILE__";
   private static readonly logFileEntryId = "__horizonLogFile__";
   private static readonly logLevelKey = "logLevel";
   private static readonly logStatusKey = "isCollectLogEnabled";
   private static readonly chromeLogPathKey = "chromeClientLogPath";

   constructor(private localStorageService: LocalStorageService) {}

   public updateLogFileInStorage = (info): string => {
      let path: string = this._savePathLocally(info);
      this.localStorageService.set(LogFileService.logStatusKey, "true");
      return path;
   };

   public getLogPathFromStorage = (): Promise<string> => {
      return new Promise((resolve) => {
         let path: string;
         if (clientUtil.isChromeClient()) {
            path = this.localStorageService.get(LogFileService.chromeLogPathKey);
            resolve(path);
         } else if (clientUtil.isIwaClient()) {
            this.getFileInfoFromIndexedDB()
               .then((fileEntry: FileSystemFileHandle) => {
                  if (!fileEntry) {
                     Logger.info("Failed to get log file fron indexedDB.");
                     resolve("");
                  }
                  resolve(fileEntry.name);
               })
               .catch((e) => {
                  Logger.error(e);
                  resolve("");
               });
         }
      });
   };

   public clearLogInfoInStorage = () => {
      this._savePathLocally(null);
      this.localStorageService.set(LogFileService.logStatusKey, "false");
      this.localStorageService.set(LogFileService.logLevelKey, Logger.LEVEL_INFO);
   };

   private _savePathLocally = (info): string => {
      let path;
      if (clientUtil.isChromeClient()) {
         path = info;
         this.localStorageService.set(LogFileService.chromeLogPathKey, info);
      } else {
         path = info ? info.folderName : null;
         this._saveFileInfoToIndexedDB(LogFileService.objectStoreName, info?.entry ? info.entry : null);
      }
      return path;
   };

   private _saveFileInfoToIndexedDB = (storeName, fileEntry) => {
      let data = {
         fileEntry: fileEntry,
         folderId: LogFileService.logFileEntryId,
         version: 1
      } as any;
      HorizonIndexedDBService.set(LogFileService.dateBaseName, storeName, "iwa_default", data)
         .then((res) => {
            if (res) {
               Logger.info("succeeded to save log file enrty in datebase.");
            } else {
               Logger.error("Failed to save log file enrty in datebase.");
            }
         })
         .catch((e) => {
            Logger.error(e);
            Logger.error("Failed to save log file enrty in datebase.");
         });
   };

   public getFileInfoFromIndexedDB = (): Promise<FileSystemFileHandle> => {
      return new Promise((resolve) => {
         HorizonIndexedDBService.get(
            LogFileService.dateBaseName,
            LogFileService.objectStoreName,
            LogFileService.logFileEntryId
         )
            .then(async (res: any) => {
               resolve(res.fileEntry);
            })
            .catch((e) => {
               resolve(null);
               Logger.debug(e);
            });
      });
   };

   public formatLogLevel = (logLevel: string | number): string | number => {
      let result: string | number;
      if (typeof logLevel === "string") {
         result = Number(logLevel);
      } else if (typeof logLevel === "number") {
         result = String(logLevel);
      }
      return result;
   };
}
